import { Alert, Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import type { FC } from 'react';
import { DashboardCard } from '~/components/molecules/DashboardCard';
import { getDashboardDescription } from '~/constants/dashboards';
import { useDashboards } from '~/hooks/useDashboards';

interface DashboardGridProps {
  onSelect: (id: string) => void;
}

export const DashboardGrid: FC<DashboardGridProps> = ({ onSelect }) => {
  const { dashboards, isLoading, error, refetch } = useDashboards();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load dashboards
        </Alert>
      </Container>
    );
  }

  if (dashboards.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          No dashboards available
        </Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 3,
          justifyContent: 'center',
        }}
      >
        {dashboards.map((dashboard) => (
          <DashboardCard
            key={dashboard.id}
            id={dashboard.id}
            name={dashboard.name}
            description={getDashboardDescription(dashboard.name)}
            onSelect={onSelect}
          />
        ))}
      </Box>
    </Container>
  );
};
